// A gesture's source-param frames: drawn straight onto the fills showing the source, landed in Redux once when it ends.
import { IS_LOCAL } from '~/lib/constants'
import { parseLivePath } from '~/lib/params'

// Bound by the store at its creation, never imported: automation imports this module, and the store's middleware imports automation.
let store = null
export const bindSourceWrites = s => {
	store = s
}

const held = new Map()

/** A running gesture's frames on `source`, by key. */
export const heldValues = source => {
	const out = {}
	for (const [path, v] of held) {
		const p = parseLivePath(path)
		if (p.source === source) out[p.key] = v
	}
	return out
}

// The bag a running fade shows per source this frame: what a gesture or loop draws over instead of Redux's FROM bag.
const fading = new Map()

/** The bag on screen for `source`: a fade's blend, else Redux's. */
export const shownSourceValues = source => fading.get(source) ?? store?.getState().live.source_settings?.[source] ?? {}

/** The keys a write for `source` draws over the bag on screen (a gesture's frames, a loop's samples): what a path fade's target takes. */
export const editsOver = (source, values) => {
	const shown = shownSourceValues(source)
	return Object.fromEntries(Object.entries(values).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(shown[k])))
}

/** One fade frame of `source`'s bag, drawn under any held frames. */
export const showFadeSource = (source, values) => {
	fading.set(source, values)
	renderSource(source)
}

/** The fade settled or died: Redux's bags are on screen again. */
export const clearFadeSources = () => fading.clear()

/** Redraws `source`'s fills from the bag on screen under any held frames. */
export const renderSource = source =>
	window.pixi_write_source?.(source, { ...shownSourceValues(source), ...heldValues(source) })

/** One frame of a gesture on `source_settings.<source>.<key>`. */
export const holdSourceWrite = (path, value) => {
	const p = parseLivePath(path)
	if (p?.scope !== 'source') return
	held.set(path, value)
	renderSource(p.source)
}

/** The gesture's end: its frames as one write, under the history entry its start pushed. */
export const settleSourceWrites = () => {
	if (!held.size) return
	const payload = [...held]
	held.clear()
	store?.dispatch({ type: 'live/setLiveProperties', payload, meta: { skipHistory: true } })
}

// Dev only — the rig drives a panel gesture through the page's own instance; a console `import()` can load a second one (and a second store).
if (IS_LOCAL && typeof window !== 'undefined')
	window.source_writes = { holdSourceWrite, settleSourceWrites, heldValues }
